/**
 * Phase 35 Session 35.2a — HelpClientProvider.
 *
 * Ergonomic React provider that wires the transport singleton + sets
 * the active locale + (in 35.2b) hooks up the realtime listener.
 * Optional — apps that prefer to call `setHelpTransport(client)`
 * directly at boot can skip the provider.
 *
 * Why optional + not required:
 *   - Some surfaces want to read help content from a non-React code
 *     path (e.g. service-layer prefetch in pos-core, automation
 *     preview in the dashboard). The singleton approach in `client.ts`
 *     is reachable from any code; React providers aren't.
 *   - The provider wraps the singleton — it's a layer of sugar, not
 *     a competing source of truth.
 *   - React Query's QueryClientProvider is the consumer's
 *     responsibility. We don't bundle it here because every app
 *     already has its own query client (with surface-specific stale
 *     times, retry policies, etc.).
 */
import { useEffect, type ReactNode } from 'react';
import type { AxiosInstance } from 'axios';
import {
  registerTooltipFallbacks,
  type TooltipFallback,
} from './fallbacks/fallbacks';
import {
  setHelpTransport,
  resetHelpTransport,
  isHelpTransportConfigured,
} from './client';
import { useHelpClientStore } from './store/helpClientStore';

export interface HelpClientProviderProps {
  /** AxiosInstance the help-client should issue requests against.
   *  Each app supplies its own (web apps: from `apps/<app>/src/lib/
   *  axios.ts`; RN apps: from `@commerce-os/rn-shared`'s
   *  `getApiClient()`). */
  transport: AxiosInstance;
  /** BCP-47 locale; defaults to en-US when omitted. */
  locale?: string;
  /** Build-bundled tooltip fallbacks (35.2b's MDX plugins generate
   *  this array). When omitted, the runtime fallback registry stays
   *  empty + `useTooltip` falls through to `null` on network error.
   *  Apps without bundled fallbacks should pass `[]` explicitly to
   *  avoid the lint warning. */
  fallbacks?: readonly TooltipFallback[];
  /** When true (default), every hook respects gating + permission.
   *  When false, the hooks short-circuit to fallback / null without
   *  hitting the transport — used by storefront pre-auth + offline
   *  mode. */
  networkEnabled?: boolean;
  children?: ReactNode;
}

/**
 * Wraps the help-client setup into one component. Mount once at app
 * root, above whatever component tree consumes the help hooks.
 */
export function HelpClientProvider(props: HelpClientProviderProps): ReactNode {
  const {
    transport,
    locale = 'en-US',
    fallbacks,
    networkEnabled = true,
    children,
  } = props;

  // Transport registration — fires synchronously so the first render
  // can already dispatch a fetch. Effect cleanup resets the singleton
  // so unmounting + remounting a different transport works (HMR).
  useEffect(() => {
    setHelpTransport(transport);
    return () => {
      // Only reset if we're the registered owner; otherwise a sibling
      // provider that registered last wins.
      if (isHelpTransportConfigured()) resetHelpTransport();
    };
  }, [transport]);

  useEffect(() => {
    useHelpClientStore.getState().setLocale(locale);
  }, [locale]);

  useEffect(() => {
    useHelpClientStore.getState().setDisableNetwork(!networkEnabled);
  }, [networkEnabled]);

  useEffect(() => {
    if (fallbacks) {
      registerTooltipFallbacks(fallbacks);
    }
  }, [fallbacks]);

  return children ?? null;
}
